import { useEffect, useMemo, useState } from "react";
import { Button, Divider, Modal, Typography, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  type Classifications,
  type Commitment,
  type DsmScopeItem
} from "design-scope";
import { SidepanelStyles } from "constants/index";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { toDesignScopeUpdateInstance } from "../shared/design-scope-create-mappers";
import { DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE } from "../shared/design-scope-messages";
import {
  buildBulkBaseline,
  diffBulkScopeForm,
  hasBulkScopeChanges,
  mergeBulkScopeInput,
  validateBulkScopeDraft,
  type BulkScopeForm
} from "../shared/bulk-edit-design-scope-merge";
import ClassificationPicker from "./classification-picker";
import ScopeBidPackagesEditor from "./scope-bid-packages-editor";
import ScopeDesignPackageRow from "./scope-design-package-row";
import { blankCommitment, Field, Section } from "./scope-item-field";
import ScopeResponsibilitySelect from "./scope-responsibility-select";
import {
  findResponsibilityOption,
  type DesignScopeResponsibilityOption
} from "../shared/design-scope-responsibilities";
import type { AddDesignScopeResponsibility } from "../shared/design-scope-responsibilities";
import type { ProjectEntities } from "../hooks/use-project-entities";
import {
  defaultLodIdFromOptions,
  lodForOptionId,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import "./scope-item.scss";

export type BulkEditDesignScopeDetailsProps = {
  open: boolean;
  selectedRows: DsmScopeItem[];
  entities: ProjectEntities;
  lodOptions: LevelOfDetailOption[];
  lodOptionsLoading?: boolean;
  /** Project responsibility catalog. Omit to keep the built-in list. */
  responsibilities?: DesignScopeResponsibilityOption[];
  responsibilitiesLoading?: boolean;
  onAddResponsibility?: AddDesignScopeResponsibility;
  projectToken: string;
  onClose: () => void;
  onUpdateDone: () => void | Promise<void>;
};

export default function BulkEditDesignScopeDetails(
  props: BulkEditDesignScopeDetailsProps
) {
  const {
    open,
    selectedRows,
    entities,
    lodOptions,
    lodOptionsLoading,
    responsibilities,
    responsibilitiesLoading,
    onAddResponsibility,
    projectToken,
    onClose,
    onUpdateDone
  } = props;

  const [baseline, setBaseline] = useState<BulkScopeForm>(() =>
    buildBulkBaseline([])
  );
  const [draft, setDraft] = useState<BulkScopeForm>(() =>
    buildBulkBaseline([])
  );
  const [inProgress, setInProgress] = useState(false);

  const selectionKey = selectedRows.map((r) => r.id).join(",");

  useEffect(() => {
    if (!open) return;
    if (selectedRows.length === 0) {
      onClose();
      return;
    }
    const next = buildBulkBaseline(selectedRows);
    setBaseline(next);
    setDraft(next);
    // Reset only when the panel opens or the selected id set changes — not
    // when the parent recreates callback props.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey
  }, [open, selectionKey]);

  const defaultLodId = useMemo(
    () => defaultLodIdFromOptions(lodOptions),
    [lodOptions]
  );

  // Fill LOD on blank commitment rows once options arrive (same as modal).
  useEffect(() => {
    if (!open || !defaultLodId) return;
    setDraft((prev) => {
      if (!prev.commitments) return prev;
      let changed = false;
      const next = prev.commitments.map((c) => {
        if (c.levelOfDetailValueId) return c;
        changed = true;
        return {
          ...c,
          levelOfDetailValueId: defaultLodId,
          lod: lodForOptionId(lodOptions, defaultLodId)
        };
      });
      return changed ? { ...prev, commitments: next } : prev;
    });
  }, [open, defaultLodId, lodOptions]);

  const changes = useMemo(
    () => diffBulkScopeForm(baseline, draft),
    [baseline, draft]
  );
  const isDiff = hasBulkScopeChanges(changes);
  const canApply =
    isDiff &&
    selectedRows.length > 0 &&
    validateBulkScopeDraft(draft, changes, {
      lodOptionsLoading,
      lodOptionsCount: lodOptions.length
    });

  const patchDraft = (patch: Partial<BulkScopeForm>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const dpOptions = entities.designPackages.map((d) => ({
    value: d.id,
    label: d.label
  }));
  const bpOptions = useMemo(
    () =>
      entities.bidPackages.map((b) => ({
        value: b.id,
        label: b.label
      })),
    [entities.bidPackages]
  );

  const lodDisabled = Boolean(lodOptionsLoading) || lodOptions.length === 0;
  const commitments = draft.commitments ?? [];

  const patchCommit = (i: number, patch: Partial<Commitment>) => {
    const list = [...(draft.commitments ?? [])];
    list[i] = { ...list[i]!, ...patch };
    patchDraft({ commitments: list });
  };

  const setCommitmentLod = (i: number, levelOfDetailValueId: string) => {
    patchCommit(i, {
      levelOfDetailValueId,
      lod: lodForOptionId(lodOptions, levelOfDetailValueId)
    });
  };

  const selectedNames = selectedRows.map((r) => ({
    id: r.id,
    name: r.title?.trim() || r.description || r.id
  }));

  const onApply = async () => {
    if (!canApply || inProgress) return;
    setInProgress(true);
    try {
      const feature_instances = selectedRows.map((row) => {
        const input = mergeBulkScopeInput(row, draft, changes);
        if (responsibilities) {
          const name = input.responsibility.trim();
          const match = name
            ? findResponsibilityOption(responsibilities, name)
            : undefined;
          if (match) input.responsibleDesignerId = match.id;
          else if (!name) input.responsibleDesignerId = "";
        }
        return toDesignScopeUpdateInstance(input, row.id);
      });
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances
        },
        projectToken
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to update scope items.");
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await onUpdateDone();
    } catch (err) {
      console.error(err);
      message.error("Unable to update scope items.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Modal
      title="Bulk Edit"
      className="custom-drawer dsm-edit"
      style={SidepanelStyles.style}
      width={740}
      open={open}
      onCancel={() => {
        if (!inProgress) onClose();
      }}
      destroyOnHidden
      footer={
        <div className="flex w-full justify-end space-x-3">
          <Button
            onClick={onClose}
            disabled={inProgress}
            data-testid="scope-bulk-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canApply || inProgress}
            loading={inProgress}
            onClick={() => void onApply()}
            data-testid="scope-bulk-apply"
          >
            Apply
          </Button>
        </div>
      }
    >
      <div className="body-content-wrapper px-1">
        <div
          className="mb-3 px-1 font-semibold"
          data-testid="scope-bulk-selected-count"
        >
          <span className="tracking-wide">Selected Scope Items: </span>
          <DisplayNameListPopover list={selectedNames}>
            {selectedRows.length}
          </DisplayNameListPopover>
        </div>

        <Field label="Responsibility">
          <ScopeResponsibilitySelect
            className="max-w-full"
            style={{ width: "100%" }}
            testId="scope-bulk-responsibility"
            value={
              responsibilities
                ? (findResponsibilityOption(
                    responsibilities,
                    draft.responsibility ?? ""
                  )?.id ??
                  draft.responsibility ??
                  "")
                : (draft.responsibility ?? "")
            }
            catalog={responsibilities}
            catalogLoading={responsibilitiesLoading}
            onAdd={
              onAddResponsibility
                ? async (name) => {
                    const result = await onAddResponsibility(name);
                    if (result.success && result.name) {
                      patchDraft({ responsibility: result.name });
                    }
                    return result;
                  }
                : undefined
            }
            onChange={(next) => {
              if (!responsibilities) {
                patchDraft({ responsibility: next });
                return;
              }
              if (!next) {
                patchDraft({ responsibility: "" });
                return;
              }
              const match = responsibilities.find(
                (option) => option.id === next
              );
              if (match) patchDraft({ responsibility: match.name });
            }}
          />
        </Field>

        <Divider />

        <Section title="Classification">
          <ClassificationPicker
            value={draft.classification}
            onChange={(c: Classifications | undefined) =>
              patchDraft({ classification: c })
            }
          />
        </Section>

        <Divider />

        <Section
          title="Design packages"
          action={
            <Button
              icon={<PlusOutlined />}
              data-testid="scope-bulk-commit-add"
              onClick={() =>
                patchDraft({
                  commitments: [
                    ...commitments,
                    blankCommitment(
                      defaultLodId,
                      lodForOptionId(lodOptions, defaultLodId)
                    )
                  ]
                })
              }
            >
              Add
            </Button>
          }
        >
          <div className="flex flex-col gap-3.5">
            {commitments.map((c, i) => (
              <ScopeDesignPackageRow
                key={i}
                index={i}
                commitment={c}
                dpOptions={dpOptions}
                milestoneOptions={(
                  entities.milestonesByDp[c.designPackageId] ?? []
                ).map((m) => ({ value: m.id, label: m.label }))}
                lodOptions={lodOptions}
                lodOptionsLoading={lodOptionsLoading}
                lodDisabled={lodDisabled}
                onPatch={(patch) => patchCommit(i, patch)}
                onLodChange={(id) => setCommitmentLod(i, id)}
                onRemove={() =>
                  patchDraft({
                    commitments: commitments.filter((_, idx) => idx !== i)
                  })
                }
              />
            ))}
            {commitments.length === 0 && (
              <div
                className="rounded-xl py-6 text-center"
                style={{ border: "1px dashed var(--ciq-gray-200)" }}
              >
                <Typography.Text type="secondary">
                  No shared design packages — add to apply to all selected
                  items.
                </Typography.Text>
              </div>
            )}
          </div>
        </Section>

        <Divider />

        <ScopeBidPackagesEditor
          bids={draft.bids ?? []}
          bpOptions={bpOptions}
          onChange={(bids) => patchDraft({ bids })}
          emptyMessage="No shared Bid Packages — add to apply to all selected items."
        />
      </div>
    </Modal>
  );
}
